import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { getClassroomReport } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, Target, Keyboard, BookOpen, BarChart3
} from 'lucide-react';

const ReportsPage = () => {
  const navigate = useNavigate();
  const { classroomId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await getClassroomReport(classroomId);
        setReport(res.data);
      } catch {
        toast.error('Failed to load report');
        navigate('/teacher');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [classroomId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const students = report?.student_stats || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-white/5 bg-background-paper/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(`/classroom/${classroomId}`)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-xl font-bold">Class Reports</h1>
            <p className="text-text-secondary text-sm">{report?.classroom?.name}</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Class Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="card-glass p-6" data-testid="stat-students">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Students</p>
                <p className="font-heading text-2xl font-bold">{students.length}</p>
              </div>
            </div>
          </Card>
          
          <Card className="card-glass p-6" data-testid="stat-exercises">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-secondary/20 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Exercises</p>
                <p className="font-heading text-2xl font-bold">{report?.total_exercises || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="card-glass p-6" data-testid="stat-accuracy">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-status-success/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-status-success" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Class Accuracy</p>
                <p className="font-heading text-2xl font-bold">{report?.class_avg_accuracy || 0}%</p>
              </div>
            </div>
          </Card>
          
          <Card className="card-glass p-6" data-testid="stat-wpm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                <Keyboard className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Class WPM</p>
                <p className="font-heading text-2xl font-bold">{report?.class_avg_wpm || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="card-glass p-6" data-testid="stat-quiz">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-status-warning/20 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-status-warning" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Class Quiz</p>
                <p className="font-heading text-2xl font-bold">{report?.class_avg_quiz_score || 0}%</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Student Performance Table */}
        <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          Student Performance
        </h2>

        {students.length === 0 ? (
          <Card className="card-glass p-12 text-center" data-testid="no-students">
            <Users className="w-12 h-12 text-text-muted mx-auto mb-4" />
            <h3 className="font-heading text-lg font-bold mb-2">No student data yet</h3>
            <p className="text-text-secondary">Students need to complete exercises to see their performance here</p>
          </Card>
        ) : (
          <Card className="card-glass overflow-hidden" data-testid="students-table">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-text-secondary">Student</TableHead>
                  <TableHead className="text-text-secondary text-center">Completed</TableHead>
                  <TableHead className="text-text-secondary text-center">Avg Accuracy</TableHead>
                  <TableHead className="text-text-secondary text-center">Avg WPM</TableHead>
                  <TableHead className="text-text-secondary text-center">Avg Quiz</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {students.map((student) => (
                  <TableRow 
                    key={student.user_id}
                    className="border-white/5 hover:bg-white/5"
                    data-testid={`student-row-${student.user_id}`}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium">{student.name}</p>
                        <p className="text-text-muted text-xs">{student.email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-center font-mono">
                      <span className="text-neon-green">{student.exercises_completed}</span>
                      {student.exercises_started > student.exercises_completed && (
                        <span className="text-neon-yellow ml-1">
                          ({student.exercises_started - student.exercises_completed} in progress)
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`font-mono font-bold ${
                        student.avg_accuracy >= 80 ? 'text-neon-green' : 
                        student.avg_accuracy >= 60 ? 'text-neon-yellow' : 'text-neon-red'
                      }`}>
                        {student.avg_accuracy}%
                      </span>
                    </TableCell>
                    <TableCell className="text-center font-mono text-secondary">
                      {student.avg_wpm}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`font-mono font-bold ${
                        student.avg_quiz_score >= 80 ? 'text-neon-green' : 
                        student.avg_quiz_score >= 60 ? 'text-neon-yellow' : 'text-neon-red'
                      }`}>
                        {student.avg_quiz_score}%
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ReportsPage;
